import SearchIcon from "@mui/icons-material/Search";
import Autocomplete from "@mui/material/Autocomplete";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import { alpha, styled } from "@mui/material/styles";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Zoom from "@mui/material/Zoom";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { TransitionGroup } from "react-transition-group";
import { useIsMobileScreen } from "../hooks/useIsMobileScreen";
import {
  applyFilter,
  loadSearchResults,
  removeFilter,
  resetFilters,
  selectAppliedFilters,
  selectIsSearchInProgress,
  selectPossibleFilterIds,
  STANDALONE_FILTER_IDS,
} from "../redux/search";
import { suggest } from "../suggestions";
import DateChip from "./DateChip";
import TagsByType from "./Tag";

const Search = ({ className }) => {
  const inputRef = useRef(null);
  const [options, setOptions] = useState([]);
  const appliedFilters = useSelector(selectAppliedFilters);
  const possibleFilterIds = useSelector(selectPossibleFilterIds);
  const dispatch = useDispatch();

  const isMobileScreen = useIsMobileScreen();

  const isSearchInProgress = useSelector(selectIsSearchInProgress);

  const standaloneFilter = appliedFilters.find(({ id }) =>
    STANDALONE_FILTER_IDS.includes(id),
  );

  const effectiveFilters = standaloneFilter
    ? [
        ...(appliedFilters.length > 1
          ? [{ id: "rest", placeholder: "Other Filters", value: "..." }]
          : []),
        standaloneFilter,
      ]
    : appliedFilters;

  useEffect(() => {
    if (!isSearchInProgress) {
      inputRef.current.focus();
    }
  }, [inputRef, isSearchInProgress]);

  const onAutocompleteChange = (event, values, reason, details) => {
    if (reason === "clear") {
      dispatch(resetFilters());
    } else if (reason === "selectOption") {
      dispatch(
        applyFilter({
          id: details.option.filterType,
          value: details.option.value,
        }),
      );
    } else if (reason === "removeOption") {
      const filterToRemove = details.option;
      dispatch(removeFilter(filterToRemove.id));
    }
  };

  const onInputChange = (event, value, reason) => {
    suggest(value).then((results) => {
      setOptions(
        results
          .filter(
            ({ filterType, value }) =>
              possibleFilterIds.includes(filterType) &&
              !appliedFilters.some(
                (f) => f.id === filterType && f.value === value,
              ),
          )
          .slice(0, 10),
      );
    });
  };

  return (
    <div
      className={`${className} ${isSearchInProgress ? "SearchInProgress" : ""}`}
    >
      <Autocomplete
        autoHighlight
        disabled={isSearchInProgress}
        filterOptions={(options) => options}
        freeSolo
        fullWidth
        getOptionLabel={(option) =>
          `${option.filterType}\x00${JSON.stringify(option.value)}`
        }
        limitTags={isMobileScreen ? 1 : -1}
        multiple
        onChange={onAutocompleteChange}
        onInputChange={onInputChange}
        options={options}
        renderInput={(props) => (
          <TextField
            {...props}
            autoFocus
            inputRef={inputRef}
            placeholder={`Enter ${
              !appliedFilters.length
                ? "Date or Pax or Tkt or Customer"
                : "other filters"
            }`}
            InputProps={{
              ...props.InputProps,
              startAdornment: (
                <TransitionGroup>
                  <>
                    <InputAdornment
                      className="SearchButtonAdornment"
                      key="search-icon"
                      position="start"
                    >
                      <IconButton
                        className="SearchButton"
                        onClick={() => dispatch(loadSearchResults())}
                      >
                        <SearchIcon />
                      </IconButton>
                    </InputAdornment>
                  </>
                  {props.InputProps.startAdornment?.map((component, index) => (
                    <Zoom key={index}>{component}</Zoom>
                  ))}
                </TransitionGroup>
              ),
            }}
          />
        )}
        renderOption={(liProps, option, state) => {
          const Tag = TagsByType[option.filterType];
          return (
            Tag && (
              <li {...liProps}>
                <Tag value={option.value} />
              </li>
            )
          );
        }}
        renderTags={(values, getTagProps, ownerState) =>
          values
            .map((v, index) => {
              const Tag = TagsByType[v.id];
              const isDateFilter = ["flightDates", "saleDate"].includes(v.id);
              const CustomChip = isDateFilter ? DateChip : Chip;
              const { onDelete, ...tagProps } = getTagProps({ index });
              return (
                Tag && (
                  <Tooltip title={v.placeholder}>
                    <CustomChip
                      key={index}
                      {...tagProps}
                      onDelete={v.id !== "rest" ? onDelete : null}
                      {...(isDateFilter ? { filterType: v.id } : {})}
                      label={<Tag value={v.value} />}
                      value={v.value}
                    />
                  </Tooltip>
                )
              );
            })
            .filter((tag) => tag)
        }
        value={effectiveFilters}
      />
    </div>
  );
};

export default styled(Search)(({ theme }) => {
  const p = theme.palette.primary.main;
  const pr = theme.palette.progress.main;
  const s = theme.palette.secondary.main;
  return `
    display: flex;
    width: 100%;
    padding: 5px 8px 8px 8px;
    background-image:
      linear-gradient(90deg, ${s} 30%, ${p} 95%);
    background-size: 100% 3px;
    background-position: right 100%;
    background-repeat: no-repeat;
    background-color: ${alpha(s, 0.1)};

    &:hover {
      background-color: ${alpha(s, 0.16)};
    }

    .MuiInputBase-root.MuiOutlinedInput-root {
      padding-top: 4px;
      padding-bottom: 4px;
    }

    .MuiOutlinedInput-notchedOutline {
      border: none;
    }

    .SearchButtonAdornment {
      display: inline-flex;
    }

    .SearchButton {
      margin: 0 -7px;
    }

    &.SearchInProgress {
      animation: SearchInProgress 2500ms linear infinite;
      background-image: linear-gradient(90deg, ${s} 30%, ${p} 95%), linear-gradient(90deg, transparent 45%, ${pr} 50%, transparent 55%);
      background-size: 100% 3px, 300% 7px;
      background-position: 100% 100%, 0% 100%;
      background-repeat: no-repeat, no-repeat;
    }
    @keyframes SearchInProgress {
       0% { background-position: 100% 100%, 0% 100%; }
       50% { background-position: 100% 100%, 100% 100%; }
       100% { background-position: 100% 100%, 0% 100%; }
    }`;
});
